import { useState } from 'react';
import { Segment } from 'semantic-ui-react';
import { FileUploadButton } from '@britishcouncil/react-admin';

import { ImportStatusEnum } from 'api';
import { BulkDataType } from 'types';
import {
  useGetLastImportQuery,
  useUploadImportFileMutation,
  useLazyGetFileUrlQuery,
} from 'store/bulk-actions';

import { ErrorResultMessage, ImportResultMessage, InvalidTemplateMessage } from './ResultMessage';
import { getPollingState, getUploadErrorMessages } from './UploadSegment.utils';

export const UploadSegment = ({ bulkDataType }: { bulkDataType: BulkDataType }) => {
  const [pollingMs, setPollingMs] = useState(0);

  const [triggerGetFileUrl] = useLazyGetFileUrlQuery();
  const { data: currentImport, isFetching } = useGetLastImportQuery(bulkDataType, {
    pollingInterval: pollingMs,
  });
  const [uploadFile, { isLoading: isUploading, isError: isUploadError, error: uploadError }] =
    useUploadImportFileMutation();

  const { shouldPoll, isTimedOut, nextPollingMs } = getPollingState(currentImport);

  if (nextPollingMs !== pollingMs) {
    setPollingMs(nextPollingMs);
  }

  const isLoading = isUploading || isFetching || shouldPoll;
  const isServerError = currentImport?.status === ImportStatusEnum.Error;
  const errorMessages = getUploadErrorMessages(uploadError);

  const handleDownloadErrorFile = async () => {
    if (!currentImport?.errorFileId) return;

    try {
      const result = await triggerGetFileUrl(currentImport.errorFileId).unwrap();
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (e) {
      console.error('Error fetching error file URL', e);
    }
  };

  const showResult =
    !isLoading &&
    !isUploadError &&
    !!currentImport &&
    (currentImport.status === ImportStatusEnum.Success ||
      currentImport.status === ImportStatusEnum.ParseError) &&
    (currentImport.successfulRowsCount > 0 || currentImport.errorsRowsCount > 0);

  const showInvalidTemplate =
    !isLoading && !isUploadError && currentImport?.status === ImportStatusEnum.InvalidTemplate;

  const showError = !isLoading && (isUploadError || isTimedOut || isServerError);

  return (
    <Segment>
      <h4>Upload file</h4>

      <FileUploadButton
        fluid
        color="blue"
        opaque={false}
        disabled={isLoading}
        loading={isLoading}
        onFileSelected={(file) => {
          uploadFile({ dataType: bulkDataType, file });
        }}
        allowedExtensions={['xlsx']}
      >
        Select and upload file
      </FileUploadButton>

      {showError && (
        <ErrorResultMessage
          isTimedOut={isTimedOut}
          isServerError={isServerError}
          errorMessages={errorMessages}
        />
      )}

      {showResult && (
        <ImportResultMessage
          successfulImports={currentImport?.successfulRowsCount}
          failedImports={currentImport?.errorsRowsCount}
          onDownloadErrorFile={handleDownloadErrorFile}
        />
      )}

      {showInvalidTemplate && <InvalidTemplateMessage />}
    </Segment>
  );
};
